import React from 'react'
import Switch from './ui/switch'
import { useAuth } from '../hooks/use-auth'
import UpgradeDialog from './billing/upgrade-dialog'

export interface PublicToggleProps {
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  className?: string
}

// Visibility toggle
// Free users can also make podcasts private now. We still keep defaults elsewhere:
// - Free users default to public
// - Paid users default to private

const PublicToggle: React.FC<PublicToggleProps> = ({
  value,
  onChange,
  disabled = false,
  className = ''
}) => {
  const { user } = useAuth()
  const isFreeUser = !user || user.plan === 'free'
  const [showPaywall, setShowPaywall] = React.useState(false)

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked
    // Switch label is "Public": checked=true => public, false => private
    if (!disabled && isFreeUser && next === false) {
      // Free users: block making podcast private and show paywall dialog
      setShowPaywall(true)
      return
    }
    onChange(next)
  }

  const switchId = React.useId()

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={switchId} className="min-w-0 cursor-pointer">
          <div className="text-sm font-medium text-foreground">Public</div>
          <p className="text-xs text-foreground/60">When public the podcast is accessible by anyone.</p>
        </label>
        <Switch checked={value} onChange={handleToggle} disabled={disabled} id={switchId} />
      </div>
      {/* Paywall for private podcasts (free users) */}
      <UpgradeDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        title="Keep your content private"
        description={undefined}
        bullets={[
          "Private RSS feed for your podcast app",
          "Episodes hidden from public discovery",
          "Perfect for teams, classes, or personal use",
          "Share links only with people you choose"
        ]}
        paywallId="private_podcasts"
      />
    </div>
  )
}

export default PublicToggle
